import { Injectable } from '@angular/core';
import { Capacitor } from '@capacitor/core';
import { Health, HealthDataType, HealthSample } from '@capgo/capacitor-health';
import { HealthSteps } from '@elitetrainr/capacitor-health-steps';
import * as Sentry from '@sentry/angular-ivy';
import { AppService } from './app.service';
import { FitnessService, userStatTypes } from './fitness.service';
import { HelperService } from './helper.service';
import { StorageService } from './storage.service';
import { TranslateConfigService } from './translate-config.service';
import { UserService } from './user.service';

export const APPLE_HEALTH_READ_TYPES: HealthDataType[] = [
  'weight',
  'bodyFat',
  'steps',
  'calories',
  /** Android Total Calories Burned; iOS CapGo maps this to active — we prefer basal+active there. */
  'totalCalories',
  /** iOS basalEnergyBurned (used with active to derive total / restingCalories). */
  'basalCalories',
  'sleep',
  'workouts',
  /** Apple Exercise Time (iOS-only CapGo type). */
  'exerciseTime',
  'distance',
  'flightsClimbed',
  'restingHeartRate',
  'heartRate',
  'heartRateVariability',
  'vo2Max',
  'bloodPressure',
  'oxygenSaturation',
  'respiratoryRate',
  'height',
  'dietaryWater',
  'dietaryEnergyConsumed',
  'bloodGlucose',
  'bodyTemperature',
  'basalBodyTemperature',
];

/** Same CapGo read scopes on iOS (HealthKit) and Android (Health Connect). */
export const DEVICE_HEALTH_READ_TYPES = APPLE_HEALTH_READ_TYPES;

export type HealthProvider = 'appleHealth' | 'healthConnect';

export type AppleHealthSyncMetric =
  | 'weight'
  | 'sleep'
  | 'steps'
  | 'calories'
  | 'bodyFat'
  | 'distance'
  | 'flightsClimbed'
  | 'restingCalories'
  | 'activeMinutes'
  | 'restingHeartRate'
  | 'heartRate'
  | 'hrv'
  | 'vo2Max'
  | 'bloodPressure'
  | 'spo2'
  | 'respiratoryRate'
  | 'height'
  | 'water'
  | 'bloodGlucose'
  | 'bodyTemperature'
  | 'basalBodyTemperature'
  | 'caloricIntake';

export type AppleHealthSyncStepStatus = 'pending' | 'syncing' | 'done' | 'empty' | 'error';

export interface AppleHealthConnection {
  enabled: boolean;
  connectedAt?: string;
  disconnectedAt?: string;
  lastSyncAt?: string;
  lastSync?: AppleHealthSyncSummary;
  /** Raw steps query diagnostics from the last sync attempt. */
  lastStepsDebug?: string | null;
  /** Which native store this connection row belongs to. */
  provider?: HealthProvider;
}

export interface AppleHealthWeight {
  kg: number;
  date: string;
  sourceName?: string;
}

type SyncSummaryValue = { value: number; unit: string; date: string; sourceName?: string } | null;

export interface AppleHealthSyncSummary {
  weight?: SyncSummaryValue;
  sleep?: SyncSummaryValue;
  lightSleep?: SyncSummaryValue;
  deepSleep?: SyncSummaryValue;
  remSleep?: SyncSummaryValue;
  overnightHr?: SyncSummaryValue;
  overnightHrv?: SyncSummaryValue;
  steps?: { value: number; unit: string; date: string; debug?: string | null } | null;
  calories?: SyncSummaryValue;
  /** Total energy expended for the day (active + basal on iOS; TotalCaloriesBurned on Android). */
  totalCalories?: SyncSummaryValue;
  bodyFat?: SyncSummaryValue;
  distance?: SyncSummaryValue;
  flightsClimbed?: SyncSummaryValue;
  restingCalories?: SyncSummaryValue;
  activeMinutes?: SyncSummaryValue;
  restingHeartRate?: SyncSummaryValue;
  heartRate?: SyncSummaryValue;
  hrv?: SyncSummaryValue;
  vo2Max?: SyncSummaryValue;
  bpSystolic?: SyncSummaryValue;
  bpDiastolic?: SyncSummaryValue;
  spo2?: SyncSummaryValue;
  respiratoryRate?: SyncSummaryValue;
  height?: SyncSummaryValue;
  water?: SyncSummaryValue;
  bloodGlucose?: SyncSummaryValue;
  bodyTemperature?: SyncSummaryValue;
  basalBodyTemperature?: SyncSummaryValue;
  caloricIntake?: SyncSummaryValue;
}

/** Per wake-day sleep totals + stage hours + overnight window bounds. */
type SleepDayBreakdown = {
  totalHours: number;
  lightHours: number;
  deepHours: number;
  remHours: number;
  windowStartMs: number;
  windowEndMs: number;
};

export interface AppleHealthSyncStep {
  key: AppleHealthSyncMetric;
  status: AppleHealthSyncStepStatus;
  detail?: string;
}

const CONNECTION_KEYS: Record<HealthProvider, string> = {
  appleHealth: 'appleHealthConnection',
  healthConnect: 'healthConnectConnection',
};
/** Local-only: upsell shown once per app install (connect or dismiss). */
const UPSELL_SEEN_KEYS: Record<HealthProvider, string> = {
  appleHealth: 'appleHealthUpsellSeen',
  healthConnect: 'healthConnectUpsellSeen',
};
const WEIGHT_LOOKBACK_MS = 2 * 365 * 24 * 60 * 60 * 1000;
/** Max days to backfill on first connect or after a long gap. */
const MAX_BACKFILL_DAYS = 30;
const DAY_MS = 24 * 60 * 60 * 1000;
/** Min time between automatic foreground syncs. */
const SYNC_THROTTLE_MS = 15 * 60 * 1000;
const ASLEEP_STATES = new Set(['asleep', 'rem', 'deep', 'light']);

@Injectable({
  providedIn: 'root'
})
export class AppleHealthService {

  private syncInFlight = false;

  constructor(
    private appService: AppService,
    private fitnessService: FitnessService,
    private helperService: HelperService,
    private storageService: StorageService,
    private translateConfig: TranslateConfigService,
    private userService: UserService,
  ) {}

  isNativeIos(): boolean {
    return Capacitor.isNativePlatform() && Capacitor.getPlatform() === 'ios';
  }

  isNativeAndroid(): boolean {
    return Capacitor.isNativePlatform() && Capacitor.getPlatform() === 'android';
  }

  /** iOS HealthKit or Android Health Connect. */
  isNativeHealthPlatform(): boolean {
    return this.isNativeIos() || this.isNativeAndroid();
  }

  getProvider(): HealthProvider | null {
    if (this.isNativeIos()) {
      return 'appleHealth';
    }
    if (this.isNativeAndroid()) {
      return 'healthConnect';
    }
    return null;
  }

  /** RTDB `source` written on synced userStats rows. */
  getStatSource(): HealthProvider | null {
    return this.getProvider();
  }

  private connectionKey(provider?: HealthProvider | null): string | null {
    const resolved = provider || this.getProvider();
    return resolved ? CONNECTION_KEYS[resolved] : null;
  }

  private upsellSeenKey(provider?: HealthProvider | null): string | null {
    const resolved = provider || this.getProvider();
    return resolved ? UPSELL_SEEN_KEYS[resolved] : null;
  }

  private sentryFeature(): string {
    return this.isNativeAndroid() ? 'google-health' : 'apple-health';
  }

  private weightLookbackMs(): number {
    // Request history access on Android; without it Health Connect still caps near 30 days.
    return WEIGHT_LOOKBACK_MS;
  }

  async isAvailable(): Promise<boolean> {
    if (!this.isNativeHealthPlatform()) {
      return false;
    }
    try {
      const result = await Health.isAvailable();
      return !!result?.available;
    } catch (error) {
      Sentry.captureException(error?.originalError || error, {
        tags: { feature: this.sentryFeature(), step: 'is-available' },
      });
      return false;
    }
  }

  /** Android: open Health Connect install / settings when the APK is missing. */
  async openHealthConnectSettings(): Promise<void> {
    if (!this.isNativeAndroid()) {
      return;
    }
    try {
      await Health.openHealthConnectSettings();
    } catch (error) {
      Sentry.captureException(error?.originalError || error, {
        tags: { feature: this.sentryFeature(), step: 'open-health-connect-settings' },
      });
    }
  }

  async getConnection(_userId?: string): Promise<AppleHealthConnection> {
    const provider = this.getProvider();
    const key = this.connectionKey(provider);
    if (!key) {
      return { enabled: false };
    }
    try {
      const saved = await this.storageService.get(key);
      return {
        enabled: !!saved?.enabled,
        connectedAt: saved?.connectedAt,
        disconnectedAt: saved?.disconnectedAt,
        lastSyncAt: saved?.lastSyncAt,
        lastSync: saved?.lastSync || null,
        lastStepsDebug: saved?.lastStepsDebug || null,
        provider: provider || saved?.provider,
      };
    } catch (error) {
      Sentry.captureException(error?.originalError || error, {
        tags: { feature: this.sentryFeature(), step: 'get-connection' },
      });
      return { enabled: false, provider: provider || undefined };
    }
  }

  private async saveConnection(connection: AppleHealthConnection): Promise<void> {
    const provider = connection.provider || this.getProvider();
    const key = this.connectionKey(provider);
    if (!key) {
      return;
    }
    await this.storageService.set(key, {
      ...connection,
      provider,
    });
  }

  async connect(_userId?: string): Promise<void> {
    const provider = this.getProvider();
    if (!provider) {
      throw new Error('Device health sync is only available on iOS or Android');
    }

    const available = await this.isAvailable();
    if (!available) {
      if (this.isNativeAndroid()) {
        throw new Error('Health Connect is not available on this device');
      }
      throw new Error('Apple Health is not available on this device');
    }

    await Health.requestAuthorization({
      read: DEVICE_HEALTH_READ_TYPES,
      write: [],
      // Android: unlock >30 day reads for weight / body fat when the provider supports it.
      ...(this.isNativeAndroid() ? { requestHistoryAccess: true } : {}),
    });

    const current = await this.getConnection();
    await this.saveConnection({
      ...current,
      enabled: true,
      provider,
      connectedAt: new Date().toISOString(),
      disconnectedAt: null,
    });
  }

  async disconnect(_userId?: string): Promise<void> {
    const current = await this.getConnection();
    await this.saveConnection({
      ...current,
      enabled: false,
      provider: current.provider || this.getProvider() || undefined,
      disconnectedAt: new Date().toISOString(),
    });
  }

  async dismissPrompt(_userId?: string): Promise<void> {
    // Upsell is once-per-app-install; keep this on device only.
    await this.markUpsellSeen();
  }

  async markUpsellSeen(): Promise<void> {
    const key = this.upsellSeenKey();
    if (!key) {
      return;
    }
    await this.storageService.set(key, true);
  }

  async hasSeenUpsell(): Promise<boolean> {
    const key = this.upsellSeenKey();
    if (!key) {
      return true;
    }
    return !!(await this.storageService.get(key));
  }

  async shouldPrompt(_userId?: string): Promise<boolean> {
    if (!this.isNativeHealthPlatform()) {
      return false;
    }
    // Local only: survives login/logout, clears on uninstall/reinstall.
    if (await this.hasSeenUpsell()) {
      return false;
    }
    return this.isAvailable();
  }

  async getLatestWeight(): Promise<AppleHealthWeight | null> {
    if (!this.isNativeHealthPlatform()) {
      return null;
    }

    try {
      const { samples } = await Health.readSamples({
        dataType: 'weight',
        startDate: new Date(Date.now() - this.weightLookbackMs()).toISOString(),
        endDate: new Date().toISOString(),
        limit: 20,
        ascending: false,
      });
      const sample = this.newestSample(samples);
      if (!sample || sample.value == null || isNaN(Number(sample.value))) {
        return null;
      }
      return {
        kg: Number(sample.value),
        date: sample.endDate || sample.startDate,
        sourceName: sample.sourceName,
      };
    } catch (error) {
      Sentry.captureException(error?.originalError || error, {
        tags: { feature: this.sentryFeature(), step: 'read-weight' },
      });
      return null;
    }
  }

  formatWeight(kg: number, units: 'imperial' | 'metric' = 'imperial'): string {
    if (units === 'metric') {
      return `${this.helperService.roundNumber(kg, 1)} kg`;
    }
    return `${this.helperService.kgToLbs(kg)} lbs`;
  }

  getDefaultSyncSteps(): AppleHealthSyncStep[] {
    return [
      { key: 'weight', status: 'pending' },
      { key: 'sleep', status: 'pending' },
      { key: 'steps', status: 'pending' },
      { key: 'calories', status: 'pending' },
      { key: 'bodyFat', status: 'pending' },
      { key: 'distance', status: 'pending' },
      { key: 'flightsClimbed', status: 'pending' },
      { key: 'restingCalories', status: 'pending' },
      { key: 'activeMinutes', status: 'pending' },
      { key: 'restingHeartRate', status: 'pending' },
      { key: 'heartRate', status: 'pending' },
      { key: 'hrv', status: 'pending' },
      { key: 'vo2Max', status: 'pending' },
      { key: 'bloodPressure', status: 'pending' },
      { key: 'spo2', status: 'pending' },
      { key: 'respiratoryRate', status: 'pending' },
      { key: 'height', status: 'pending' },
      { key: 'water', status: 'pending' },
      { key: 'bloodGlucose', status: 'pending' },
      { key: 'bodyTemperature', status: 'pending' },
      { key: 'basalBodyTemperature', status: 'pending' },
      { key: 'caloricIntake', status: 'pending' },
    ];
  }

  /**
   * Foreground auto-sync when connected and last sync is older than the throttle.
   * Silent / no UI; safe to call on resume and after auth.
   */
  async syncIfDue(userId?: string): Promise<'synced' | 'skipped' | 'disabled'> {
    if (!this.isNativeHealthPlatform() || this.syncInFlight) {
      return 'skipped';
    }

    const uid = userId || this.userService.getAppUser()?.uid;
    if (!uid) {
      return 'disabled';
    }

    try {
      const connection = await this.getConnection(uid);
      if (!connection.enabled) {
        return 'disabled';
      }
      if (connection.lastSyncAt) {
        const ageMs = Date.now() - new Date(connection.lastSyncAt).getTime();
        if (!isNaN(ageMs) && ageMs < SYNC_THROTTLE_MS) {
          return 'skipped';
        }
      }
      await this.syncAll(uid, undefined, { quiet: true });
      return 'synced';
    } catch (error) {
      Sentry.captureException(error?.originalError || error, {
        tags: { feature: this.sentryFeature(), step: 'sync-if-due' },
      });
      return 'skipped';
    }
  }

  async syncAll(
    userId: string,
    onProgress?: (steps: AppleHealthSyncStep[]) => void,
    options?: { quiet?: boolean },
  ): Promise<AppleHealthSyncSummary> {
    if (!this.isNativeHealthPlatform()) {
      throw new Error('Device health sync is only available on iOS or Android');
    }
    if (this.syncInFlight) {
      throw new Error('Health sync already in progress');
    }
    this.syncInFlight = true;

    const quiet = !!options?.quiet;
    const steps = this.getDefaultSyncSteps();
    const summary: AppleHealthSyncSummary = {};
    const units = this.userService.getAppUser()?.preferedUnits || 'imperial';
    const statSource = this.getStatSource() || 'appleHealth';
    const provider = this.getProvider() || undefined;
    const priorConnection = await this.getConnection(userId);
    const lookbackMs = this.resolveBackfillLookbackMs(priorConnection.lastSyncAt);

    const emit = () => onProgress?.(steps.map((step) => ({ ...step })));

    try {
    // Re-prompt for any types not yet decided (won't re-show for already denied).
    try {
      await Health.requestAuthorization({
        read: DEVICE_HEALTH_READ_TYPES,
        write: [],
        ...(this.isNativeAndroid() ? { requestHistoryAccess: true } : {}),
      });
    } catch (error) {
      Sentry.captureException(error?.originalError || error, {
        tags: { feature: this.sentryFeature(), step: 'sync-reauth' },
      });
    }

    for (let i = 0; i < steps.length; i++) {
      steps[i].status = 'syncing';
      emit();
      if (!quiet) {
        await this.pause(900);
      }

      try {
        if (steps[i].key === 'weight') {
          await this.syncWeightBackfill(userId, units, statSource, summary, steps[i], lookbackMs);
        } else if (steps[i].key === 'sleep') {
          await this.syncSleepBackfill(userId, statSource, summary, steps[i], lookbackMs);
        } else if (steps[i].key === 'steps') {
          await this.syncStepsBackfill(userId, statSource, summary, steps[i], lookbackMs);
        } else if (steps[i].key === 'calories') {
          await this.syncCaloriesBackfill(userId, statSource, summary, steps[i], lookbackMs);
        } else if (steps[i].key === 'bodyFat') {
          await this.syncLatestSampleMetric(userId, {
            dataType: 'bodyFat',
            statKey: 'bodyFat',
            summaryKey: 'bodyFat',
            unit: '%',
            decimals: 1,
            fillIfEmpty: true,
            lookbackMs: Math.max(lookbackMs, this.weightLookbackMs()),
            source: statSource,
            summary,
            step: steps[i],
            detail: (v) => `${v}%`,
            backfillDays: true,
          });
        } else if (steps[i].key === 'distance') {
          await this.syncDailyDistance(userId, units, statSource, summary, steps[i], lookbackMs);
        } else if (steps[i].key === 'flightsClimbed') {
          await this.syncDailySumMetric(userId, {
            dataType: 'flightsClimbed',
            statKey: 'flightsClimbed',
            summaryKey: 'flightsClimbed',
            unit: 'flights',
            decimals: 0,
            lookbackMs,
            source: statSource,
            summary,
            step: steps[i],
            detail: (v) => `${Math.round(v)} flights`,
          });
        } else if (steps[i].key === 'restingCalories') {
          await this.syncDailySumMetric(userId, {
            dataType: 'basalCalories',
            statKey: 'restingCalories',
            summaryKey: 'restingCalories',
            unit: 'kcal',
            decimals: 0,
            lookbackMs,
            source: statSource,
            summary,
            step: steps[i],
            detail: (v) => `${Math.round(v)} kcal`,
          });
        } else if (steps[i].key === 'activeMinutes') {
          await this.syncActiveMinutes(
            userId,
            statSource,
            summary,
            steps[i],
            lookbackMs,
          );
        } else if (steps[i].key === 'restingHeartRate') {
          await this.syncLatestSampleMetric(userId, {
            dataType: 'restingHeartRate',
            statKey: 'restingHeartRate',
            summaryKey: 'restingHeartRate',
            unit: 'bpm',
            decimals: 0,
            fillIfEmpty: true,
            lookbackMs,
            source: statSource,
            summary,
            step: steps[i],
            detail: (v) => `${Math.round(v)} bpm`,
            backfillDays: true,
          });
        } else if (steps[i].key === 'heartRate') {
          await this.syncDailyAverageMetric(userId, {
            dataType: 'heartRate',
            statKey: 'heartRate',
            summaryKey: 'heartRate',
            unit: 'bpm',
            decimals: 0,
            lookbackMs,
            source: statSource,
            summary,
            step: steps[i],
            detail: (v) => `${Math.round(v)} bpm avg`,
          });
        } else if (steps[i].key === 'hrv') {
          await this.syncLatestSampleMetric(userId, {
            dataType: 'heartRateVariability',
            statKey: 'hrv',
            summaryKey: 'hrv',
            unit: 'ms',
            decimals: 1,
            fillIfEmpty: true,
            lookbackMs,
            source: statSource,
            summary,
            step: steps[i],
            detail: (v) => `${v} ms`,
            backfillDays: true,
          });
        } else if (steps[i].key === 'vo2Max') {
          await this.syncLatestSampleMetric(userId, {
            dataType: 'vo2Max',
            statKey: 'vo2Max',
            summaryKey: 'vo2Max',
            unit: 'mL/min/kg',
            decimals: 1,
            fillIfEmpty: true,
            lookbackMs: Math.max(lookbackMs, this.weightLookbackMs()),
            source: statSource,
            summary,
            step: steps[i],
            detail: (v) => `${v}`,
            backfillDays: true,
          });
        } else if (steps[i].key === 'bloodPressure') {
          await this.syncLatestBloodPressure(userId, statSource, summary, steps[i], lookbackMs);
        } else if (steps[i].key === 'spo2') {
          await this.syncLatestSampleMetric(userId, {
            dataType: 'oxygenSaturation',
            statKey: 'spo2',
            summaryKey: 'spo2',
            unit: '%',
            decimals: 1,
            fillIfEmpty: true,
            lookbackMs,
            source: statSource,
            summary,
            step: steps[i],
            detail: (v) => `${v}%`,
            backfillDays: true,
          });
        } else if (steps[i].key === 'respiratoryRate') {
          await this.syncLatestSampleMetric(userId, {
            dataType: 'respiratoryRate',
            statKey: 'respiratoryRate',
            summaryKey: 'respiratoryRate',
            unit: '/min',
            decimals: 1,
            fillIfEmpty: true,
            lookbackMs,
            source: statSource,
            summary,
            step: steps[i],
            detail: (v) => `${v} /min`,
            backfillDays: true,
          });
        } else if (steps[i].key === 'height') {
          await this.syncLatestHeight(userId, units, statSource, summary, steps[i]);
        } else if (steps[i].key === 'water') {
          await this.syncDailyWater(userId, units, statSource, summary, steps[i], lookbackMs);
        } else if (steps[i].key === 'bloodGlucose') {
          await this.syncLatestSampleMetric(userId, {
            dataType: 'bloodGlucose',
            statKey: 'bloodGlucose',
            summaryKey: 'bloodGlucose',
            unit: 'mg/dL',
            decimals: 1,
            fillIfEmpty: true,
            lookbackMs,
            source: statSource,
            summary,
            step: steps[i],
            detail: (v) => `${v} mg/dL`,
            backfillDays: true,
          });
        } else if (steps[i].key === 'bodyTemperature') {
          await this.syncLatestTemperature(userId, {
            dataType: 'bodyTemperature',
            statKey: 'bodyTemperature',
            summaryKey: 'bodyTemperature',
            units,
            lookbackMs,
            source: statSource,
            summary,
            step: steps[i],
          });
        } else if (steps[i].key === 'basalBodyTemperature') {
          await this.syncLatestTemperature(userId, {
            dataType: 'basalBodyTemperature',
            statKey: 'basalBodyTemperature',
            summaryKey: 'basalBodyTemperature',
            units,
            lookbackMs,
            source: statSource,
            summary,
            step: steps[i],
          });
        } else if (steps[i].key === 'caloricIntake') {
          await this.syncLatestSampleMetric(userId, {
            dataType: 'dietaryEnergyConsumed',
            statKey: 'caloricIntake',
            summaryKey: 'caloricIntake',
            unit: 'kcal',
            decimals: 0,
            fillIfEmpty: true,
            dailySum: true,
            lookbackMs,
            source: statSource,
            summary,
            step: steps[i],
            detail: (v) => `${Math.round(v)} kcal in`,
          });
        }
      } catch (error) {
        steps[i].status = 'error';
        Sentry.captureException(error?.originalError || error, {
          tags: { feature: this.sentryFeature(), step: `sync-${steps[i].key}` },
        });
      }

      emit();
      if (!quiet) {
        await this.pause(650);
      }
    }

    const syncedAt = new Date().toISOString();
    const current = await this.getConnection();
    await this.saveConnection({
      ...current,
      enabled: true,
      provider,
      lastSyncAt: syncedAt,
      lastSync: summary,
      lastStepsDebug: summary.steps?.debug || (summary.steps?.value > 0 ? null : current.lastStepsDebug) || null,
    });

    try {
      await this.userService.updateDBUser(userId, { healthLastSyncedAt: syncedAt });
    } catch (error) {
      Sentry.captureException((error as any)?.originalError || error, {
        tags: { feature: this.sentryFeature(), step: 'persist-health-last-synced' },
      });
    }

    return summary;
    } finally {
      this.syncInFlight = false;
    }
  }

  /**
   * Days to read from Health: from last successful sync (plus 1-day overlap),
   * capped at MAX_BACKFILL_DAYS. First sync uses the full cap.
   */
  private resolveBackfillLookbackMs(lastSyncAt?: string): number {
    const maxMs = MAX_BACKFILL_DAYS * DAY_MS;
    if (!lastSyncAt) {
      return maxMs;
    }
    const ageMs = Date.now() - new Date(lastSyncAt).getTime();
    if (isNaN(ageMs) || ageMs < DAY_MS) {
      return DAY_MS;
    }
    return Math.min(ageMs + DAY_MS, maxMs);
  }

  private async syncWeightBackfill(
    userId: string,
    units: string,
    source: string,
    summary: AppleHealthSyncSummary,
    step: AppleHealthSyncStep,
    lookbackMs: number,
  ): Promise<void> {
    const samples = await this.readSamplesInWindow('weight', Math.max(lookbackMs, this.weightLookbackMs()), 200);
    const newestByDate = new Map<string, HealthSample>();
    for (const sample of samples) {
      if (sample.value == null || isNaN(Number(sample.value))) {
        continue;
      }
      const date = this.toDateKey(sample.endDate || sample.startDate);
      if (!date) {
        continue;
      }
      const existing = newestByDate.get(date);
      if (!existing || this.sampleTime(sample) >= this.sampleTime(existing)) {
        newestByDate.set(date, sample);
      }
    }
    if (!newestByDate.size) {
      summary.weight = null;
      step.status = 'empty';
      return;
    }

    const metric = units === 'metric';
    const unit = metric ? 'kg' : 'lbs';
    let latest: SyncSummaryValue = null;
    for (const date of Array.from(newestByDate.keys()).sort()) {
      const sample = newestByDate.get(date)!;
      const kg = Number(sample.value);
      const value = metric
        ? this.helperService.roundNumber(kg, 1)
        : this.helperService.kgToLbs(kg);
      await this.upsertStatIfEmpty(userId, 'weight', {
        value,
        unit,
        date,
        notes: sample.sourceName || null,
        source,
        sourceName: sample.sourceName || null,
      });
      latest = { value, unit, date, sourceName: sample.sourceName };
    }
    summary.weight = latest;
    step.status = 'done';
    const preferredUnits: 'imperial' | 'metric' = units === 'metric' ? 'metric' : 'imperial';
    step.detail = latest
      ? `${newestByDate.size}d · ${this.formatWeight(Number(newestByDate.get(latest.date)!.value), preferredUnits)}`
      : undefined;
  }

  private async syncSleepBackfill(
    userId: string,
    source: string,
    summary: AppleHealthSyncSummary,
    step: AppleHealthSyncStep,
    lookbackMs: number,
  ): Promise<void> {
    const byDate = await this.getSleepBreakdownByDate(lookbackMs);
    if (!byDate.size) {
      summary.sleep = null;
      summary.lightSleep = null;
      summary.deepSleep = null;
      summary.remSleep = null;
      summary.overnightHr = null;
      summary.overnightHrv = null;
      step.status = 'empty';
      return;
    }

    let latestSleep: SyncSummaryValue = null;
    let latestLight: SyncSummaryValue = null;
    let latestDeep: SyncSummaryValue = null;
    let latestRem: SyncSummaryValue = null;
    let stageDays = 0;

    for (const date of Array.from(byDate.keys()).sort()) {
      const day = byDate.get(date)!;
      await this.upsertStatIfEmpty(userId, 'sleep', {
        value: day.totalHours,
        unit: 'hours',
        date,
        source,
      });
      latestSleep = { value: day.totalHours, unit: 'hours', date };

      const stageWrites: Array<{ key: userStatTypes; hours: number }> = [
        { key: 'lightSleep', hours: day.lightHours },
        { key: 'deepSleep', hours: day.deepHours },
        { key: 'remSleep', hours: day.remHours },
      ];
      let wroteStage = false;
      for (const stage of stageWrites) {
        if (stage.hours <= 0) {
          continue;
        }
        await this.fitnessService.upsertUserStatByDate(userId, stage.key, {
          value: stage.hours,
          unit: 'hours',
          date,
          source,
        });
        wroteStage = true;
        const payload = { value: stage.hours, unit: 'hours', date };
        if (stage.key === 'lightSleep') {
          latestLight = payload;
        } else if (stage.key === 'deepSleep') {
          latestDeep = payload;
        } else {
          latestRem = payload;
        }
      }
      if (wroteStage) {
        stageDays += 1;
      }
    }

    const overnight = await this.syncOvernightVitalsFromSleepWindows(
      userId,
      source,
      byDate,
      lookbackMs,
    );

    summary.sleep = latestSleep;
    summary.lightSleep = latestLight;
    summary.deepSleep = latestDeep;
    summary.remSleep = latestRem;
    summary.overnightHr = overnight.hr;
    summary.overnightHrv = overnight.hrv;
    step.status = 'done';
    const parts = [`${byDate.size}d · ${latestSleep?.value ?? '—'} hrs`];
    if (stageDays) {
      parts.push(`${stageDays}d stages`);
    }
    if (overnight.hrDays || overnight.hrvDays) {
      parts.push(`${overnight.hrDays}/${overnight.hrvDays}d overnight`);
    }
    step.detail = parts.join(' · ');
  }

  private async syncStepsBackfill(
    userId: string,
    source: string,
    summary: AppleHealthSyncSummary,
    step: AppleHealthSyncStep,
    lookbackMs: number,
  ): Promise<void> {
    const { byDate, debug } = await this.getStepsByDate(lookbackMs);
    if (!byDate.size) {
      summary.steps = {
        value: 0,
        unit: 'steps',
        date: this.toDateKey(new Date().toISOString()),
        debug: debug || 'no-debug-returned',
      };
      step.status = 'empty';
      step.detail = debug || 'no-debug-returned';
      return;
    }

    const today = this.toDateKey(new Date().toISOString());
    let written = 0;
    let preferred: { value: number; date: string } | null = null;
    for (const date of Array.from(byDate.keys()).sort()) {
      const value = Math.round(byDate.get(date) || 0);
      if (value <= 0) {
        continue;
      }
      try {
        await this.fitnessService.upsertUserStatByDate(userId, 'steps', {
          value,
          unit: 'steps',
          date,
          source,
        });
        written += 1;
        preferred = { value, date };
      } catch (error) {
        Sentry.captureException((error as any)?.originalError || error, {
          tags: { feature: this.sentryFeature(), step: 'steps-upsert' },
        });
      }
    }

    if (!written || !preferred) {
      summary.steps = {
        value: 0,
        unit: 'steps',
        date: today,
        debug: debug || 'upsert-empty',
      };
      step.status = 'empty';
      step.detail = debug || 'upsert-empty';
      return;
    }

    const todayValue = byDate.get(today);
    if (todayValue && todayValue > 0) {
      preferred = { value: Math.round(todayValue), date: today };
    }
    summary.steps = {
      value: preferred.value,
      unit: 'steps',
      date: preferred.date,
      debug,
    };
    step.status = 'done';
    step.detail = `${written}d · ${preferred.value.toLocaleString()} steps`;
  }

  private async syncCaloriesBackfill(
    userId: string,
    source: string,
    summary: AppleHealthSyncSummary,
    step: AppleHealthSyncStep,
    lookbackMs: number,
  ): Promise<void> {
    const activeByDate = await this.sumDailySamplesByDate('calories', lookbackMs);
    let written = 0;
    let preferredActive: { value: number; date: string } | null = null;
    for (const date of Array.from(activeByDate.keys()).sort()) {
      const value = Math.round(activeByDate.get(date) || 0);
      if (value <= 0) {
        continue;
      }
      await this.fitnessService.upsertUserStatByDate(userId, 'calories', {
        value,
        unit: 'kcal',
        date,
        source,
      });
      written += 1;
      preferredActive = { value, date };
    }

    if (preferredActive) {
      summary.calories = {
        value: preferredActive.value,
        unit: 'kcal',
        date: preferredActive.date,
      };
    } else {
      summary.calories = null;
    }

    try {
      let totalByDate: Map<string, number>;
      if (this.isNativeAndroid()) {
        totalByDate = await this.sumDailySamplesByDate('totalCalories', lookbackMs);
      } else {
        const basalByDate = await this.sumDailySamplesByDate('basalCalories', lookbackMs);
        totalByDate = new Map<string, number>();
        const dates = new Set([
          ...Array.from(activeByDate.keys()),
          ...Array.from(basalByDate.keys()),
        ]);
        dates.forEach((date) => {
          const active = activeByDate.get(date) || 0;
          const basal = basalByDate.get(date) || 0;
          // Require basal so total !== active-only when basal is missing.
          if (basal > 0) {
            const total = active + basal;
            if (total > 0) {
              totalByDate.set(date, total);
            }
          }
        });
      }

      let preferredTotal: { value: number; date: string } | null = null;
      for (const date of Array.from(totalByDate.keys()).sort()) {
        const value = Math.round(totalByDate.get(date) || 0);
        if (value <= 0) {
          continue;
        }
        await this.fitnessService.upsertUserStatByDate(userId, 'totalCalories', {
          value,
          unit: 'kcal',
          date,
          source,
        });
        written += 1;
        preferredTotal = { value, date };
      }
      summary.totalCalories = preferredTotal
        ? { value: preferredTotal.value, unit: 'kcal', date: preferredTotal.date }
        : null;
    } catch (error) {
      summary.totalCalories = null;
      Sentry.captureException((error as any)?.originalError || error, {
        tags: { feature: this.sentryFeature(), step: 'total-calories' },
      });
    }

    if (!summary.calories && !summary.totalCalories) {
      step.status = 'empty';
      return;
    }
    step.status = 'done';
    step.detail = `${written || 1}d · ${summary.calories?.value ?? '—'} / ${summary.totalCalories?.value ?? '—'} kcal`;
  }

  /**
   * Build per wake-day sleep totals + light/deep/REM hours + overnight window.
   * Prefer CapGo nested `stages[]` when present; otherwise bucket top-level sleepState.
   * Generic `asleep` counts toward total only (not invented stage splits).
   */
  private async getSleepBreakdownByDate(
    lookbackMs: number,
  ): Promise<Map<string, SleepDayBreakdown>> {
    const asleepByDate = new Map<string, number>();
    const inBedByDate = new Map<string, number>();
    const lightByDate = new Map<string, number>();
    const deepByDate = new Map<string, number>();
    const remByDate = new Map<string, number>();
    const asleepWindowStart = new Map<string, number>();
    const asleepWindowEnd = new Map<string, number>();
    const inBedWindowStart = new Map<string, number>();
    const inBedWindowEnd = new Map<string, number>();

    const addMinutes = (map: Map<string, number>, date: string, minutes: number) => {
      map.set(date, (map.get(date) || 0) + minutes);
    };
    const expandWindow = (
      startMap: Map<string, number>,
      endMap: Map<string, number>,
      date: string,
      startMs: number,
      endMs: number,
    ) => {
      if (!startMs || !endMs || endMs < startMs) {
        return;
      }
      const prevStart = startMap.get(date);
      const prevEnd = endMap.get(date);
      startMap.set(date, prevStart == null ? startMs : Math.min(prevStart, startMs));
      endMap.set(date, prevEnd == null ? endMs : Math.max(prevEnd, endMs));
    };
    const addStageMinutes = (date: string, state: string | undefined, minutes: number) => {
      if (!minutes || minutes <= 0) {
        return;
      }
      if (state === 'light') {
        addMinutes(lightByDate, date, minutes);
      } else if (state === 'deep') {
        addMinutes(deepByDate, date, minutes);
      } else if (state === 'rem') {
        addMinutes(remByDate, date, minutes);
      }
    };

    try {
      const { samples } = await Health.readSamples({
        dataType: 'sleep',
        startDate: new Date(Date.now() - lookbackMs).toISOString(),
        endDate: new Date().toISOString(),
        limit: 1000,
        ascending: false,
      });
      for (const sample of samples || []) {
        const date = this.toDateKey(sample.endDate || sample.startDate);
        if (!date) {
          continue;
        }
        const nestedStages = sample.stages || [];
        // Prefer nested CapGo stages when present; empty hasStageData falls through to top-level.
        if (nestedStages.length) {
          for (const stage of nestedStages) {
            const stageState = stage?.stage;
            if (stageState === 'awake') {
              continue;
            }
            const minutes = Number(stage?.durationMinutes);
            if (!minutes || isNaN(minutes) || minutes <= 0) {
              continue;
            }
            const stageStart = new Date(stage.startDate || sample.startDate).getTime();
            const stageEnd = new Date(stage.endDate || sample.endDate || stage.startDate).getTime();
            if (stageState === 'inBed') {
              addMinutes(inBedByDate, date, minutes);
              expandWindow(inBedWindowStart, inBedWindowEnd, date, stageStart, stageEnd);
              continue;
            }
            if (!stageState || ASLEEP_STATES.has(stageState)) {
              addMinutes(asleepByDate, date, minutes);
              addStageMinutes(date, stageState, minutes);
              expandWindow(asleepWindowStart, asleepWindowEnd, date, stageStart, stageEnd);
            }
          }
          continue;
        }

        const minutes = Number(sample.value);
        if (!minutes || isNaN(minutes) || minutes <= 0) {
          continue;
        }
        const state = sample.sleepState;
        if (state === 'awake') {
          continue;
        }
        const startMs = new Date(sample.startDate || sample.endDate).getTime();
        const endMs = new Date(sample.endDate || sample.startDate).getTime();
        if (state === 'inBed') {
          addMinutes(inBedByDate, date, minutes);
          expandWindow(inBedWindowStart, inBedWindowEnd, date, startMs, endMs);
          continue;
        }
        if (!state || ASLEEP_STATES.has(state)) {
          addMinutes(asleepByDate, date, minutes);
          addStageMinutes(date, state, minutes);
          expandWindow(asleepWindowStart, asleepWindowEnd, date, startMs, endMs);
        }
      }
    } catch (error) {
      Sentry.captureException((error as any)?.originalError || error, {
        tags: { feature: this.sentryFeature(), step: 'sleep-backfill' },
      });
    }

    const out = new Map<string, SleepDayBreakdown>();
    const dates = new Set([
      ...Array.from(asleepByDate.keys()),
      ...Array.from(inBedByDate.keys()),
    ]);
    dates.forEach((date) => {
      const asleepMinutes = asleepByDate.get(date) || 0;
      const inBedMinutes = inBedByDate.get(date) || 0;
      const totalMinutes = asleepMinutes || inBedMinutes;
      if (totalMinutes <= 0) {
        return;
      }
      const useAsleepWindow = asleepMinutes > 0
        && asleepWindowStart.has(date)
        && asleepWindowEnd.has(date);
      const windowStartMs = useAsleepWindow
        ? asleepWindowStart.get(date)!
        : (inBedWindowStart.get(date) || 0);
      const windowEndMs = useAsleepWindow
        ? asleepWindowEnd.get(date)!
        : (inBedWindowEnd.get(date) || 0);
      out.set(date, {
        totalHours: this.helperService.roundNumber(totalMinutes / 60, 1),
        lightHours: this.helperService.roundNumber((lightByDate.get(date) || 0) / 60, 1),
        deepHours: this.helperService.roundNumber((deepByDate.get(date) || 0) / 60, 1),
        remHours: this.helperService.roundNumber((remByDate.get(date) || 0) / 60, 1),
        windowStartMs,
        windowEndMs,
      });
    });
    return out;
  }

  /**
   * Average CapGo HR / HRV samples whose timestamps fall inside each sleep window.
   * Distinct from calendar-day heartRate / hrv rollups.
   */
  private async syncOvernightVitalsFromSleepWindows(
    userId: string,
    source: string,
    sleepByDate: Map<string, SleepDayBreakdown>,
    lookbackMs: number,
  ): Promise<{
    hr: SyncSummaryValue;
    hrv: SyncSummaryValue;
    hrDays: number;
    hrvDays: number;
  }> {
    const hrSamples = await this.readSamplesInWindow('heartRate', lookbackMs, 5000);
    const hrvSamples = await this.readSamplesInWindow('heartRateVariability', lookbackMs, 2000);

    let latestHr: SyncSummaryValue = null;
    let latestHrv: SyncSummaryValue = null;
    let hrDays = 0;
    let hrvDays = 0;

    for (const date of Array.from(sleepByDate.keys()).sort()) {
      const day = sleepByDate.get(date)!;
      if (!day.windowStartMs || !day.windowEndMs || day.windowEndMs < day.windowStartMs) {
        continue;
      }

      const hrAvg = this.averageSamplesInWindow(hrSamples, day.windowStartMs, day.windowEndMs);
      if (hrAvg != null && hrAvg > 0) {
        const value = this.helperService.roundNumber(hrAvg, 0);
        await this.fitnessService.upsertUserStatByDate(userId, 'overnightHr', {
          value,
          unit: 'bpm',
          date,
          source,
        });
        latestHr = { value, unit: 'bpm', date };
        hrDays += 1;
      }

      const hrvAvg = this.averageSamplesInWindow(hrvSamples, day.windowStartMs, day.windowEndMs);
      if (hrvAvg != null && hrvAvg > 0) {
        const value = this.helperService.roundNumber(hrvAvg, 1);
        await this.fitnessService.upsertUserStatByDate(userId, 'overnightHrv', {
          value,
          unit: 'ms',
          date,
          source,
        });
        latestHrv = { value, unit: 'ms', date };
        hrvDays += 1;
      }
    }

    return { hr: latestHr, hrv: latestHrv, hrDays, hrvDays };
  }

  private averageSamplesInWindow(
    samples: HealthSample[],
    windowStartMs: number,
    windowEndMs: number,
  ): number | null {
    let sum = 0;
    let count = 0;
    for (const sample of samples || []) {
      const value = Number(sample.value);
      if (isNaN(value) || value <= 0) {
        continue;
      }
      const time = this.sampleTime(sample);
      if (!time || time < windowStartMs || time > windowEndMs) {
        continue;
      }
      sum += value;
      count += 1;
    }
    if (!count) {
      return null;
    }
    return sum / count;
  }

  private async getStepsByDate(
    lookbackMs: number,
  ): Promise<{ byDate: Map<string, number>; debug: string }> {
    const byDate = new Map<string, number>();
    const debugParts: string[] = [];
    const today = this.toDateKey(new Date().toISOString());
    const now = new Date();

    if (Capacitor.getPlatform() === 'ios') {
      try {
        const native = await HealthSteps.getStepCount();
        debugParts.push(native?.debug || `native:${native?.source || 'HealthSteps'}`);
        if (native?.value > 0) {
          byDate.set(native.date || today, native.value);
        }
      } catch (error) {
        debugParts.push(`nativeErr:${(error as any)?.message || error}`);
      }
    }

    try {
      const result = await Health.queryAggregated({
        dataType: 'steps',
        startDate: new Date(Date.now() - lookbackMs).toISOString(),
        endDate: now.toISOString(),
        bucket: 'day',
        aggregation: 'sum',
      });
      const samples = this.unwrapSamples(result);
      debugParts.push(`agg:${samples.length}`);
      for (const sample of samples) {
        const date = this.toDateKey(sample.startDate || sample.endDate);
        const value = Number(sample.value ?? sample.values?.sum);
        if (!date || isNaN(value) || value <= 0) {
          continue;
        }
        // Prefer native today reading when present.
        if (date === today && byDate.has(today)) {
          continue;
        }
        byDate.set(date, value);
      }
    } catch (error) {
      debugParts.push(`aggErr:${(error as any)?.message || error}`);
    }

    if (!byDate.size) {
      try {
        const raw = await this.sumDailySamplesByDate('steps', lookbackMs);
        raw.forEach((value, date) => byDate.set(date, value));
        debugParts.push(`raw:${byDate.size}`);
      } catch (error) {
        debugParts.push(`rawErr:${(error as any)?.message || error}`);
      }
    }

    return { byDate, debug: debugParts.join(' | ') };
  }

  private async readSamplesInWindow(
    dataType: HealthDataType,
    lookbackMs: number,
    limit = 500,
  ): Promise<HealthSample[]> {
    try {
      const { samples } = await Health.readSamples({
        dataType,
        startDate: new Date(Date.now() - lookbackMs).toISOString(),
        endDate: new Date().toISOString(),
        limit,
        ascending: false,
      });
      return samples || [];
    } catch (error) {
      Sentry.captureException((error as any)?.originalError || error, {
        tags: { feature: this.sentryFeature(), step: `read-window-${dataType}` },
      });
      return [];
    }
  }

  private sampleTime(sample: HealthSample): number {
    return new Date(sample.endDate || sample.startDate).getTime() || 0;
  }

  /**
   * Public steps probe for Connected Apps diagnostics.
   * Always returns a debug string so the UI never falls back to "no-debug".
   */
  async probeSteps(): Promise<{ value: number; date: string; debug: string }> {
    try {
      const result = await this.getLatestDailySteps();
      return {
        value: result?.value || 0,
        date: result?.date || this.toDateKey(new Date().toISOString()),
        debug: result?.debug || 'probe-empty-debug',
      };
    } catch (error) {
      return {
        value: 0,
        date: this.toDateKey(new Date().toISOString()),
        debug: `probeThrow:${(error as any)?.message || error}`,
      };
    }
  }

  private unwrapSamples(result: any): any[] {
    if (!result) {
      return [];
    }
    if (Array.isArray(result)) {
      return result;
    }
    if (Array.isArray(result.samples)) {
      return result.samples;
    }
    if (Array.isArray(result.value?.samples)) {
      return result.value.samples;
    }
    return [];
  }

  private async getLatestDailySteps(): Promise<{ value: number; date: string; debug?: string } | null> {
    const today = this.toDateKey(new Date().toISOString());
    const debugParts: string[] = [];

    // Capgo JS type is "steps" → HealthKit HKQuantityTypeIdentifier.stepCount.
    // Query stepCount directly with HKStatisticsQuery; Capgo aggregation has been empty mid-day.
    if (Capacitor.getPlatform() === 'ios') {
      try {
        const native = await HealthSteps.getStepCount();
        debugParts.push(native?.debug || `native:${native?.source || 'HealthSteps'}`);
        if (native?.value > 0) {
          return {
            value: native.value,
            date: native.date || today,
            debug: debugParts.join(' | '),
          };
        }
      } catch (error) {
        debugParts.push(`nativeErr:${(error as any)?.message || error}`);
      }
    }

    const now = new Date();
    const startOfToday = new Date(now);
    startOfToday.setHours(0, 0, 0, 0);

    try {
      const auth = await Health.checkAuthorization({
        read: ['steps', 'weight', 'sleep'],
        write: [],
      });
      debugParts.push(
        `authSteps:${(auth?.readAuthorized || []).includes('steps') ? 'yes' : 'no'}`,
        `authDenied:${(auth?.readDenied || []).join(',') || 'none'}`,
      );
    } catch (error) {
      debugParts.push(`authErr:${(error as any)?.message || error}`);
    }

    // Capgo fallbacks (same HealthKit stepCount under dataType: 'steps')
    try {
      const result = await Health.queryAggregated({
        dataType: 'steps',
        startDate: startOfToday.toISOString(),
        endDate: now.toISOString(),
        bucket: 'hour',
        aggregation: 'sum',
      });
      const samples = this.unwrapSamples(result);
      const total = samples.reduce((sum, sample) => {
        const value = Number(sample?.value ?? sample?.values?.sum);
        return sum + (isNaN(value) ? 0 : value);
      }, 0);
      debugParts.push(`hour:${samples.length}/${Math.round(total)}`);
      if (total > 0) {
        return { value: total, date: today, debug: debugParts.join(' | ') };
      }
    } catch (error) {
      debugParts.push(`hourErr:${(error as any)?.message || error}`);
    }

    try {
      const result = await Health.queryAggregated({
        dataType: 'steps',
        startDate: new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString(),
        endDate: now.toISOString(),
        bucket: 'day',
        aggregation: 'sum',
      });
      const samples = this.unwrapSamples(result);
      debugParts.push(`agg:${samples.length}`);
      const totals = samples
        .map((sample) => ({
          date: this.toDateKey(sample.startDate || sample.endDate),
          value: Number(sample.value ?? sample.values?.sum),
        }))
        .filter((entry) => entry.date && !isNaN(entry.value) && entry.value > 0);
      const preferred = totals.find((entry) => entry.date === today)
        || totals.sort((a, b) => b.date.localeCompare(a.date))[0];
      if (preferred) {
        return { value: preferred.value, date: preferred.date, debug: debugParts.join(' | ') };
      }
    } catch (error) {
      debugParts.push(`aggErr:${(error as any)?.message || error}`);
    }

    try {
      const result = await Health.readSamples({
        dataType: 'steps',
        startDate: new Date(Date.now() - 7 * 24 * 60 * 60 * 1000).toISOString(),
        endDate: now.toISOString(),
        limit: 1000,
        ascending: false,
      });
      const samples = this.unwrapSamples(result);
      debugParts.push(`raw7:${samples.length}`);
      if (samples.length) {
        const byDate = new Map<string, number>();
        for (const sample of samples) {
          const value = Number(sample?.value);
          if (isNaN(value) || value <= 0) {
            continue;
          }
          const date = this.toDateKey(sample.startDate || sample.endDate);
          byDate.set(date, (byDate.get(date) || 0) + value);
        }
        const preferredDate = byDate.has(today)
          ? today
          : Array.from(byDate.keys()).sort().reverse()[0];
        if (preferredDate && (byDate.get(preferredDate) || 0) > 0) {
          return {
            value: byDate.get(preferredDate),
            date: preferredDate,
            debug: debugParts.join(' | '),
          };
        }
      }
    } catch (error) {
      debugParts.push(`raw7Err:${(error as any)?.message || error}`);
    }

    const debug = debugParts.join(' | ') || 'no-queries-ran';
    Sentry.captureMessage('Device health steps empty', {
      level: 'warning',
      tags: { feature: this.sentryFeature(), step: 'steps-empty' },
      extra: { debug },
    });

    return { value: 0, date: today, debug };
  }

  private async getLatestDailyCalories(): Promise<{ value: number; date: string } | null> {
    const today = this.toDateKey(new Date().toISOString());
    const startDate = new Date(Date.now() - 7 * 24 * 60 * 60 * 1000).toISOString();
    const endDate = new Date().toISOString();

    try {
      const { samples } = await Health.queryAggregated({
        dataType: 'calories',
        startDate,
        endDate,
        bucket: 'day',
        aggregation: 'sum',
      });
      const totals = (samples || [])
        .map((sample) => ({
          date: this.toDateKey(sample.startDate || sample.endDate),
          value: Number(sample.value ?? sample.values?.sum),
        }))
        .filter((entry) => entry.date && !isNaN(entry.value) && entry.value > 0);
      const preferred = totals.find((entry) => entry.date === today) || totals.sort((a, b) => b.date.localeCompare(a.date))[0];
      if (preferred) {
        return preferred;
      }
    } catch (error) {
      Sentry.captureException((error as any)?.originalError || error, {
        tags: { feature: this.sentryFeature(), step: 'calories-aggregated' },
      });
    }

    try {
      const { samples } = await Health.readSamples({
        dataType: 'calories',
        startDate,
        endDate,
        limit: 1000,
        ascending: false,
      });
      if (!samples?.length) {
        return null;
      }
      const byDate = new Map<string, number>();
      for (const sample of samples) {
        const value = Number(sample.value);
        if (isNaN(value) || value <= 0) {
          continue;
        }
        const date = this.toDateKey(sample.startDate || sample.endDate);
        byDate.set(date, (byDate.get(date) || 0) + value);
      }
      const preferredDate = byDate.has(today)
        ? today
        : Array.from(byDate.keys()).sort().reverse()[0];
      if (preferredDate && (byDate.get(preferredDate) || 0) > 0) {
        return { value: byDate.get(preferredDate), date: preferredDate };
      }
    } catch (error) {
      Sentry.captureException((error as any)?.originalError || error, {
        tags: { feature: this.sentryFeature(), step: 'calories-samples' },
      });
    }

    return null;
  }

  /**
   * Daily total energy expended for the Active / Total day-summary tile.
   * - Android: CapGo `totalCalories` → Health Connect TotalCaloriesBurned
   * - iOS: CapGo `totalCalories` incorrectly maps to activeEnergyBurned, so we
   *   derive total as active + basalEnergyBurned (`basalCalories`) for the day
   */
  private async getLatestDailyTotalCalories(
    activeCalories?: { value: number; date: string } | null,
  ): Promise<{ value: number; date: string } | null> {
    const today = this.toDateKey(new Date().toISOString());

    if (this.isNativeAndroid()) {
      const byDate = await this.sumDailySamplesByDate('totalCalories');
      return this.pickPreferredDailyTotal(byDate, today, activeCalories?.date);
    }

    // iOS: active + basal for a shared preferred date.
    // Require basal samples so we don't persist total === active when basal is missing.
    const basalByDate = await this.sumDailySamplesByDate('basalCalories');
    if (!basalByDate.size) {
      return null;
    }

    const preferredDate = (activeCalories?.date && basalByDate.has(activeCalories.date))
      ? activeCalories.date
      : (basalByDate.has(today)
        ? today
        : Array.from(basalByDate.keys()).sort().reverse()[0]);
    const active = (activeCalories?.date === preferredDate ? activeCalories.value : 0) || 0;
    const basal = basalByDate.get(preferredDate) || 0;
    const total = active + basal;
    if (total <= 0) {
      return null;
    }
    return { value: total, date: preferredDate };
  }

  private async sumDailySamplesByDate(
    dataType: HealthDataType,
    lookbackMs: number = MAX_BACKFILL_DAYS * DAY_MS,
  ): Promise<Map<string, number>> {
    const byDate = new Map<string, number>();
    try {
      const { samples } = await Health.readSamples({
        dataType,
        startDate: new Date(Date.now() - lookbackMs).toISOString(),
        endDate: new Date().toISOString(),
        limit: 2000,
        ascending: false,
      });
      for (const sample of samples || []) {
        const value = Number(sample.value);
        if (isNaN(value) || value <= 0) {
          continue;
        }
        const date = this.toDateKey(sample.startDate || sample.endDate);
        if (!date) {
          continue;
        }
        byDate.set(date, (byDate.get(date) || 0) + value);
      }
    } catch (error) {
      Sentry.captureException((error as any)?.originalError || error, {
        tags: { feature: this.sentryFeature(), step: `sum-${dataType}` },
      });
    }
    return byDate;
  }

  private pickPreferredDailyTotal(
    byDate: Map<string, number>,
    today: string,
    preferDate?: string,
  ): { value: number; date: string } | null {
    if (!byDate.size) {
      return null;
    }
    const preferredDate = (preferDate && byDate.has(preferDate))
      ? preferDate
      : (byDate.has(today) ? today : Array.from(byDate.keys()).sort().reverse()[0]);
    const value = byDate.get(preferredDate) || 0;
    if (value <= 0) {
      return null;
    }
    return { value, date: preferredDate };
  }

  private async getLatestBodyFat(): Promise<{ percent: number; date: string; sourceName?: string } | null> {
    const { samples } = await Health.readSamples({
      dataType: 'bodyFat',
      startDate: new Date(Date.now() - this.weightLookbackMs()).toISOString(),
      endDate: new Date().toISOString(),
      limit: 20,
      ascending: false,
    });
    const sample = this.newestSample(samples);
    if (!sample || sample.value == null || isNaN(Number(sample.value))) {
      return null;
    }
    return {
      percent: this.helperService.roundNumber(Number(sample.value), 1),
      date: sample.endDate || sample.startDate,
      sourceName: sample.sourceName,
    };
  }

  private async syncDailyDistance(
    userId: string,
    units: string,
    source: string,
    summary: AppleHealthSyncSummary,
    step: AppleHealthSyncStep,
    lookbackMs: number,
  ): Promise<void> {
    const byDate = await this.sumDailySamplesByDate('distance', lookbackMs);
    if (!byDate.size) {
      summary.distance = null;
      step.status = 'empty';
      return;
    }
    const metric = units === 'metric';
    const unit = metric ? 'km' : 'mi';
    let preferred: SyncSummaryValue = null;
    let written = 0;
    for (const date of Array.from(byDate.keys()).sort()) {
      const meters = byDate.get(date) || 0;
      if (meters <= 0) {
        continue;
      }
      const value = metric
        ? this.helperService.roundNumber(meters / 1000, 2)
        : this.helperService.roundNumber(meters / 1609.344, 2);
      await this.fitnessService.upsertUserStatByDate(userId, 'distance', {
        value,
        unit,
        date,
        source,
      });
      written += 1;
      preferred = { value, unit, date };
    }
    summary.distance = preferred;
    step.status = preferred ? 'done' : 'empty';
    step.detail = preferred ? `${written}d · ${preferred.value} ${unit}` : undefined;
  }

  private async syncDailyWater(
    userId: string,
    units: string,
    source: string,
    summary: AppleHealthSyncSummary,
    step: AppleHealthSyncStep,
    lookbackMs: number,
  ): Promise<void> {
    const byDate = await this.sumDailySamplesByDate('dietaryWater', lookbackMs);
    if (!byDate.size) {
      summary.water = null;
      step.status = 'empty';
      return;
    }
    const metric = units === 'metric';
    const unit = metric ? 'L' : 'oz';
    let preferred: SyncSummaryValue = null;
    let written = 0;
    for (const date of Array.from(byDate.keys()).sort()) {
      const liters = byDate.get(date) || 0;
      if (liters <= 0) {
        continue;
      }
      const value = metric
        ? this.helperService.roundNumber(liters, 2)
        : this.helperService.roundNumber(liters * 33.814, 1);
      await this.fitnessService.upsertUserStatByDate(userId, 'water', {
        value,
        unit,
        date,
        source,
      });
      written += 1;
      preferred = { value, unit, date };
    }
    summary.water = preferred;
    step.status = preferred ? 'done' : 'empty';
    step.detail = preferred ? `${written}d · ${preferred.value} ${unit}` : undefined;
  }

  private async syncLatestHeight(
    userId: string,
    units: string,
    source: string,
    summary: AppleHealthSyncSummary,
    step: AppleHealthSyncStep,
  ): Promise<void> {
    const sample = await this.readNewestSample('height', this.weightLookbackMs());
    if (!sample || sample.value == null || isNaN(Number(sample.value))) {
      summary.height = null;
      step.status = 'empty';
      return;
    }
    const cm = Number(sample.value);
    const metric = units === 'metric';
    const value = metric
      ? this.helperService.roundNumber(cm, 1)
      : this.helperService.cmToInches(cm);
    const unit = metric ? 'cm' : 'in';
    const date = this.toDateKey(sample.endDate || sample.startDate);
    await this.upsertStatIfEmpty(userId, 'height', {
      value,
      unit,
      date,
      notes: sample.sourceName || null,
      source,
      sourceName: sample.sourceName || null,
    });
    summary.height = { value, unit, date, sourceName: sample.sourceName };
    step.status = 'done';
    step.detail = `${value} ${unit}`;
  }

  private async syncLatestTemperature(
    userId: string,
    opts: {
      dataType: HealthDataType;
      statKey: 'bodyTemperature' | 'basalBodyTemperature';
      summaryKey: 'bodyTemperature' | 'basalBodyTemperature';
      units: string;
      lookbackMs: number;
      source: string;
      summary: AppleHealthSyncSummary;
      step: AppleHealthSyncStep;
    },
  ): Promise<void> {
    const samples = await this.readSamplesInWindow(opts.dataType, opts.lookbackMs, 200);
    const newestByDate = new Map<string, HealthSample>();
    for (const sample of samples) {
      if (sample.value == null || isNaN(Number(sample.value))) {
        continue;
      }
      const date = this.toDateKey(sample.endDate || sample.startDate);
      if (!date) {
        continue;
      }
      const existing = newestByDate.get(date);
      if (!existing || this.sampleTime(sample) >= this.sampleTime(existing)) {
        newestByDate.set(date, sample);
      }
    }
    if (!newestByDate.size) {
      opts.summary[opts.summaryKey] = null;
      opts.step.status = 'empty';
      return;
    }
    const metric = opts.units === 'metric';
    const unit = metric ? '°C' : '°F';
    let latest: SyncSummaryValue = null;
    for (const date of Array.from(newestByDate.keys()).sort()) {
      const sample = newestByDate.get(date)!;
      const celsius = Number(sample.value);
      const value = metric
        ? this.helperService.roundNumber(celsius, 1)
        : this.helperService.roundNumber((celsius * 9) / 5 + 32, 1);
      await this.upsertStatIfEmpty(userId, opts.statKey, {
        value,
        unit,
        date,
        notes: sample.sourceName || null,
        source: opts.source,
        sourceName: sample.sourceName || null,
      });
      latest = { value, unit, date, sourceName: sample.sourceName };
    }
    opts.summary[opts.summaryKey] = latest;
    opts.step.status = 'done';
    opts.step.detail = latest ? `${newestByDate.size}d · ${latest.value}${unit}` : undefined;
  }

  private async syncLatestBloodPressure(
    userId: string,
    source: string,
    summary: AppleHealthSyncSummary,
    step: AppleHealthSyncStep,
    lookbackMs: number,
  ): Promise<void> {
    const samples = await this.readSamplesInWindow('bloodPressure', lookbackMs, 200);
    const newestByDate = new Map<string, HealthSample>();
    for (const sample of samples) {
      const systolic = Number(sample.systolic ?? sample.value);
      const diastolic = Number(sample.diastolic);
      if (isNaN(systolic) || systolic <= 0 || isNaN(diastolic) || diastolic <= 0) {
        continue;
      }
      const date = this.toDateKey(sample.endDate || sample.startDate);
      if (!date) {
        continue;
      }
      const existing = newestByDate.get(date);
      if (!existing || this.sampleTime(sample) >= this.sampleTime(existing)) {
        newestByDate.set(date, sample);
      }
    }
    if (!newestByDate.size) {
      summary.bpSystolic = null;
      summary.bpDiastolic = null;
      step.status = 'empty';
      return;
    }

    let latestSys: SyncSummaryValue = null;
    let latestDia: SyncSummaryValue = null;
    for (const date of Array.from(newestByDate.keys()).sort()) {
      const sample = newestByDate.get(date)!;
      const systolic = Math.round(Number(sample.systolic ?? sample.value));
      const diastolic = Math.round(Number(sample.diastolic));
      const base = {
        unit: 'mmHg',
        date,
        notes: sample.sourceName || null,
        source,
        sourceName: sample.sourceName || null,
      };
      await this.upsertStatIfEmpty(userId, 'bpSystolic', { ...base, value: systolic });
      await this.upsertStatIfEmpty(userId, 'bpDiastolic', { ...base, value: diastolic });
      latestSys = { value: systolic, unit: 'mmHg', date, sourceName: sample.sourceName };
      latestDia = { value: diastolic, unit: 'mmHg', date, sourceName: sample.sourceName };
    }
    summary.bpSystolic = latestSys;
    summary.bpDiastolic = latestDia;
    step.status = 'done';
    step.detail = latestSys && latestDia
      ? `${newestByDate.size}d · ${latestSys.value}/${latestDia.value}`
      : undefined;
  }

  /**
   * Active minutes → userStats.activeMinutes.
   * iOS: CapGo exerciseTime (Apple Exercise Time, minutes).
   * Android (and iOS empty fallback): CapGo queryWorkouts() duration sum.
   * Elite workout/cardio minutes fill gaps in Stats when sync has no samples.
   */
  private async syncActiveMinutes(
    userId: string,
    source: string,
    summary: AppleHealthSyncSummary,
    step: AppleHealthSyncStep,
    lookbackMs: number,
  ): Promise<void> {
    if (this.isNativeIos()) {
      await this.syncDailySumMetric(userId, {
        dataType: 'exerciseTime',
        statKey: 'activeMinutes',
        summaryKey: 'activeMinutes',
        unit: 'min',
        decimals: 0,
        lookbackMs,
        source,
        summary,
        step,
        detail: (v) => `${Math.round(v)} min`,
      });
      if (summary.activeMinutes) {
        return;
      }
    }
    await this.syncActiveMinutesFromWorkouts(
      userId,
      source,
      summary,
      step,
      lookbackMs,
    );
  }

  /** Sum CapGo workout durations (seconds → minutes) into activeMinutes by day. */
  private async syncActiveMinutesFromWorkouts(
    userId: string,
    source: string,
    summary: AppleHealthSyncSummary,
    step: AppleHealthSyncStep,
    lookbackMs: number,
  ): Promise<void> {
    const byDate = new Map<string, number>();
    try {
      let anchor: string | undefined;
      const startDate = new Date(Date.now() - lookbackMs).toISOString();
      const endDate = new Date().toISOString();
      // CapGo pages with an anchor; cap pages so a bad token cannot loop forever.
      for (let page = 0; page < 20; page += 1) {
        const result = await Health.queryWorkouts({
          startDate,
          endDate,
          limit: 100,
          ascending: false,
          ...(anchor ? { anchor } : {}),
        });
        for (const workout of result?.workouts || []) {
          const seconds = Number(workout.duration);
          if (isNaN(seconds) || seconds <= 0) {
            continue;
          }
          const date = this.toDateKey(workout.endDate || workout.startDate);
          if (!date) {
            continue;
          }
          byDate.set(date, (byDate.get(date) || 0) + seconds / 60);
        }
        if (!result?.anchor) {
          break;
        }
        anchor = result.anchor;
      }
    } catch (error) {
      Sentry.captureException((error as any)?.originalError || error, {
        tags: { feature: this.sentryFeature(), step: 'active-minutes-workouts' },
      });
    }

    if (!byDate.size) {
      summary.activeMinutes = null;
      step.status = 'empty';
      return;
    }

    let preferred: SyncSummaryValue = null;
    let written = 0;
    for (const date of Array.from(byDate.keys()).sort()) {
      const raw = byDate.get(date) || 0;
      if (raw <= 0) {
        continue;
      }
      const value = this.helperService.roundNumber(raw, 0);
      await this.fitnessService.upsertUserStatByDate(userId, 'activeMinutes', {
        value,
        unit: 'min',
        date,
        source,
      });
      written += 1;
      preferred = { value, unit: 'min', date };
    }
    summary.activeMinutes = preferred;
    step.status = preferred ? 'done' : 'empty';
    step.detail = preferred ? `${written}d · ${Math.round(preferred.value)} min` : undefined;
  }

  private async syncDailySumMetric(
    userId: string,
    opts: {
      dataType: HealthDataType;
      statKey: userStatTypes;
      summaryKey: keyof AppleHealthSyncSummary;
      unit: string;
      decimals: number;
      lookbackMs: number;
      source: string;
      summary: AppleHealthSyncSummary;
      step: AppleHealthSyncStep;
      detail: (value: number) => string;
      convert?: (raw: number) => number;
    },
  ): Promise<void> {
    const byDate = await this.sumDailySamplesByDate(opts.dataType, opts.lookbackMs);
    if (!byDate.size) {
      (opts.summary as any)[opts.summaryKey] = null;
      opts.step.status = 'empty';
      return;
    }
    let preferred: SyncSummaryValue = null;
    let written = 0;
    for (const date of Array.from(byDate.keys()).sort()) {
      const raw = opts.convert ? opts.convert(byDate.get(date) || 0) : (byDate.get(date) || 0);
      if (raw <= 0) {
        continue;
      }
      const value = this.helperService.roundNumber(raw, opts.decimals);
      await this.fitnessService.upsertUserStatByDate(userId, opts.statKey, {
        value,
        unit: opts.unit,
        date,
        source: opts.source,
      });
      written += 1;
      preferred = { value, unit: opts.unit, date };
    }
    (opts.summary as any)[opts.summaryKey] = preferred;
    opts.step.status = preferred ? 'done' : 'empty';
    opts.step.detail = preferred ? `${written}d · ${opts.detail(preferred.value)}` : undefined;
  }

  private async syncDailyAverageMetric(
    userId: string,
    opts: {
      dataType: HealthDataType;
      statKey: userStatTypes;
      summaryKey: keyof AppleHealthSyncSummary;
      unit: string;
      decimals: number;
      lookbackMs: number;
      source: string;
      summary: AppleHealthSyncSummary;
      step: AppleHealthSyncStep;
      detail: (value: number) => string;
    },
  ): Promise<void> {
    const averages = await this.averageDailySamplesByDate(opts.dataType, opts.lookbackMs);
    if (!averages.size) {
      (opts.summary as any)[opts.summaryKey] = null;
      opts.step.status = 'empty';
      return;
    }
    let preferred: SyncSummaryValue = null;
    let written = 0;
    for (const date of Array.from(averages.keys()).sort()) {
      const value = this.helperService.roundNumber(averages.get(date) || 0, opts.decimals);
      if (value <= 0) {
        continue;
      }
      await this.fitnessService.upsertUserStatByDate(userId, opts.statKey, {
        value,
        unit: opts.unit,
        date,
        source: opts.source,
      });
      written += 1;
      preferred = { value, unit: opts.unit, date };
    }
    (opts.summary as any)[opts.summaryKey] = preferred;
    opts.step.status = preferred ? 'done' : 'empty';
    opts.step.detail = preferred ? `${written}d · ${opts.detail(preferred.value)}` : undefined;
  }

  private async syncLatestSampleMetric(
    userId: string,
    opts: {
      dataType: HealthDataType;
      statKey: userStatTypes;
      summaryKey: keyof AppleHealthSyncSummary;
      unit: string;
      decimals: number;
      fillIfEmpty: boolean;
      dailySum?: boolean;
      lookbackMs?: number;
      backfillDays?: boolean;
      source: string;
      summary: AppleHealthSyncSummary;
      step: AppleHealthSyncStep;
      detail: (value: number) => string;
    },
  ): Promise<void> {
    const lookbackMs = opts.lookbackMs || MAX_BACKFILL_DAYS * DAY_MS;

    if (opts.dailySum) {
      const byDate = await this.sumDailySamplesByDate(opts.dataType, lookbackMs);
      if (!byDate.size) {
        (opts.summary as any)[opts.summaryKey] = null;
        opts.step.status = 'empty';
        return;
      }
      let preferred: SyncSummaryValue = null;
      let written = 0;
      for (const date of Array.from(byDate.keys()).sort()) {
        const value = this.helperService.roundNumber(byDate.get(date) || 0, opts.decimals);
        if (value <= 0) {
          continue;
        }
        const payload = {
          value,
          unit: opts.unit,
          date,
          notes: null,
          source: opts.source,
          sourceName: null,
        };
        if (opts.fillIfEmpty) {
          await this.upsertStatIfEmpty(userId, opts.statKey, payload);
        } else {
          await this.fitnessService.upsertUserStatByDate(userId, opts.statKey, payload);
        }
        written += 1;
        preferred = { value, unit: opts.unit, date };
      }
      (opts.summary as any)[opts.summaryKey] = preferred;
      opts.step.status = preferred ? 'done' : 'empty';
      opts.step.detail = preferred ? `${written}d · ${opts.detail(preferred.value)}` : undefined;
      return;
    }

    if (opts.backfillDays) {
      const samples = await this.readSamplesInWindow(opts.dataType, lookbackMs, 500);
      const newestByDate = new Map<string, HealthSample>();
      for (const sample of samples) {
        if (sample.value == null || isNaN(Number(sample.value)) || Number(sample.value) <= 0) {
          continue;
        }
        const date = this.toDateKey(sample.endDate || sample.startDate);
        if (!date) {
          continue;
        }
        const existing = newestByDate.get(date);
        if (!existing || this.sampleTime(sample) >= this.sampleTime(existing)) {
          newestByDate.set(date, sample);
        }
      }
      if (!newestByDate.size) {
        (opts.summary as any)[opts.summaryKey] = null;
        opts.step.status = 'empty';
        return;
      }
      let preferred: SyncSummaryValue = null;
      for (const date of Array.from(newestByDate.keys()).sort()) {
        const sample = newestByDate.get(date)!;
        const value = this.helperService.roundNumber(Number(sample.value), opts.decimals);
        const payload = {
          value,
          unit: opts.unit,
          date,
          notes: sample.sourceName || null,
          source: opts.source,
          sourceName: sample.sourceName || null,
        };
        if (opts.fillIfEmpty) {
          await this.upsertStatIfEmpty(userId, opts.statKey, payload);
        } else {
          await this.fitnessService.upsertUserStatByDate(userId, opts.statKey, payload);
        }
        preferred = { value, unit: opts.unit, date, sourceName: sample.sourceName };
      }
      (opts.summary as any)[opts.summaryKey] = preferred;
      opts.step.status = 'done';
      opts.step.detail = preferred
        ? `${newestByDate.size}d · ${opts.detail(preferred.value)}`
        : undefined;
      return;
    }

    const sample = await this.readNewestSample(opts.dataType, lookbackMs);
    if (!sample || sample.value == null || isNaN(Number(sample.value))) {
      (opts.summary as any)[opts.summaryKey] = null;
      opts.step.status = 'empty';
      return;
    }
    const value = this.helperService.roundNumber(Number(sample.value), opts.decimals);
    const date = this.toDateKey(sample.endDate || sample.startDate);
    const payload = {
      value,
      unit: opts.unit,
      date,
      notes: sample.sourceName || null,
      source: opts.source,
      sourceName: sample.sourceName || null,
    };
    if (opts.fillIfEmpty) {
      await this.upsertStatIfEmpty(userId, opts.statKey, payload);
    } else {
      await this.fitnessService.upsertUserStatByDate(userId, opts.statKey, payload);
    }
    (opts.summary as any)[opts.summaryKey] = {
      value,
      unit: opts.unit,
      date,
      sourceName: sample.sourceName,
    };
    opts.step.status = 'done';
    opts.step.detail = opts.detail(value);
  }

  private async readNewestSample(
    dataType: HealthDataType,
    lookbackMs: number,
  ): Promise<HealthSample | null> {
    try {
      const { samples } = await Health.readSamples({
        dataType,
        startDate: new Date(Date.now() - lookbackMs).toISOString(),
        endDate: new Date().toISOString(),
        limit: 50,
        ascending: false,
      });
      return this.newestSample(samples);
    } catch (error) {
      Sentry.captureException((error as any)?.originalError || error, {
        tags: { feature: this.sentryFeature(), step: `read-${dataType}` },
      });
      return null;
    }
  }

  private async averageDailySamplesByDate(
    dataType: HealthDataType,
    lookbackMs: number = MAX_BACKFILL_DAYS * DAY_MS,
  ): Promise<Map<string, number>> {
    const sums = new Map<string, number>();
    const counts = new Map<string, number>();
    try {
      const { samples } = await Health.readSamples({
        dataType,
        startDate: new Date(Date.now() - lookbackMs).toISOString(),
        endDate: new Date().toISOString(),
        limit: 2000,
        ascending: false,
      });
      for (const sample of samples || []) {
        const value = Number(sample.value);
        if (isNaN(value) || value <= 0) {
          continue;
        }
        const date = this.toDateKey(sample.startDate || sample.endDate);
        if (!date) {
          continue;
        }
        sums.set(date, (sums.get(date) || 0) + value);
        counts.set(date, (counts.get(date) || 0) + 1);
      }
    } catch (error) {
      Sentry.captureException((error as any)?.originalError || error, {
        tags: { feature: this.sentryFeature(), step: `avg-${dataType}` },
      });
    }
    const averages = new Map<string, number>();
    Array.from(sums.keys()).forEach((date) => {
      const count = counts.get(date) || 0;
      if (count > 0) {
        averages.set(date, (sums.get(date) || 0) / count);
      }
    });
    return averages;
  }

  private async upsertStatIfEmpty(
    userId: string,
    stat: userStatTypes,
    data: any,
  ): Promise<void> {
    const existing = await this.fitnessService.getUserStatByDate(userId, stat, data.date);
    if (existing) {
      return;
    }
    await this.fitnessService.addUserStat(userId, stat, data);
  }

  /** Local calendar YYYY-MM-DD (not UTC), so overnight sleep/steps land on the right day. */
  private toDateKey(iso: string): string {
    const date = new Date(iso);
    if (isNaN(date.getTime())) {
      return (iso || '').slice(0, 10);
    }
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  }

  private pause(ms: number): Promise<void> {
    return new Promise((resolve) => setTimeout(resolve, ms));
  }

  private newestSample(samples: HealthSample[] = []): HealthSample | null {
    if (!samples.length) {
      return null;
    }
    return samples.slice().sort((a, b) => {
      const aTime = new Date(a.endDate || a.startDate).getTime();
      const bTime = new Date(b.endDate || b.startDate).getTime();
      return bTime - aTime;
    })[0];
  }

  showDismissedHint(): void {
    const hintKey = this.isNativeAndroid()
      ? 'pages.connectedApps.googleUpsell.notNowHint'
      : 'pages.connectedApps.upsell.notNowHint';
    this.appService.showToastWithDuration(
      this.translateConfig.t(hintKey),
      4000,
      'success'
    );
  }

  /** Translation key prefix for Connected Apps / upsell copy on this platform. */
  upsellCopyPrefix(): string {
    return this.isNativeAndroid() ? 'pages.connectedApps.googleUpsell' : 'pages.connectedApps.upsell';
  }
}
